/**
 * useHorizontalCard.ts
 */
import Card from '@shared/types/props/card';
import ChannelUtils from '@shared/utils/channels';
import Content from '@shared/types/enums/content';
import Event from '@shared/classes/event';
import ImageUtils from '@shared/utils/image';
import MainContext from '@shared/components/utils/context/mainContext';
import MosaicContext from '@shared/components/utils/context/mosaicContext';
import ParentalUtils from '@shared/utils/parental';
import PhotoDefault from '@shared/assets/images/png/photoDefault.png';
import { ContextType } from '@shared/types/enums/context';
import { selectMagicRemoteEnabled } from '@shared/store/slices/magicRemote.slice';
import { useAppSelector } from '@shared/store/hooks';
import { useContext, useMemo, useRef, useState } from 'react';

/**
 * Hook for the horizontal card component.
 */
export default function useHorizontalCard({
  context = ContextType.MAIN,
  focused,
  item,
  progressBar,
  showInfo = true,
  showMask = true,
  showTitle = true,
  onClick,
}: Card) {
  // Context
  const main = useContext(MainContext);
  const mosaic = useContext(MosaicContext);
  const { hoverAsset } = context === ContextType.MAIN ? main : mosaic;

  // Selectors
  const enabled = useAppSelector(selectMagicRemoteEnabled);

  // States
  const [hovered, setHovered] = useState(false);
  const previousRetriesError = useRef(0);

  // Data
  const parentalCheck =
    !ParentalUtils.isAdultRestricted(item as Event) ||
    item.type === Content.ADDON;
  const upgradablePlan = (item as Event)?.channel
    ? ChannelUtils.getUpgradablePlan((item as Event)?.channel, false, item.type)
    : null;

  // Event handlers
  const onMouseEnter = () => {
    setHovered(true);
    hoverAsset(item);
  };

  const onMouseLeave = () => {
    setHovered(false);
  };

  const onImageError = (
    event: React.SyntheticEvent<HTMLImageElement, Event>
  ) => {
    const imgElement = event.target as HTMLImageElement;
    if (previousRetriesError.current === 0) {
      previousRetriesError.current += 1;

      imgElement.src = ImageUtils.getImage(
        (item as Event)?.channel?.card || '',
        'photo'
      );
    } else {
      imgElement.src = PhotoDefault;
    }
  };

  // Memoized data
  const { display, props } = useMemo(() => {
    return {
      display: {
        mask: showMask && showTitle,
        info: showInfo,
      },
      props: {
        container: {
          onClick,
          onMouseEnter,
          onMouseLeave,
        },
        image: {
          src: parentalCheck ? (item as Event).photo : '',
          onError: onImageError,
        },
        info: {
          focused: (!enabled && focused) || hovered,
          item,
          plan: upgradablePlan,
          progressBar,
          showTitle,
        },
      },
    };
  }, [
    onClick,
    onMouseEnter,
    onMouseLeave,
    hoverAsset,
    setHovered,
    enabled,
    hovered,
    focused,
    item,
    showMask,
    showInfo,
    showTitle,
    progressBar,
  ]);

  // Return values
  return { display, props };
}
